import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { useUser } from "../lib/UserContext";
import toast from "react-hot-toast";
import { ImagePlus, Pencil, X } from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Template {
  id: string;
  praticien_id: string;
  name: string;
  display_name: string;
  template_body: string;
  placeholders: string[];
  language_code: string;
  content_sid: string;
  header_media_url: string;
}

interface EditingState {
  id: string;
  display_name: string;
  header_media_url: string;
  // read-only fields shown in drawer
  template_body: string;
  name: string;
  language_code: string;
  placeholders: string[];
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ClinicTemplates({ embedded = false }: { embedded?: boolean }) {
  const user = useUser();

  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ─── Load templates ─────────────────────────────────────────────────────────

  async function loadTemplates() {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("clinic_templates")
      .select("*")
      .eq("praticien_id", user.id)
      .eq("template_kind", "whatsapp")
      .order("display_name");
    if (error) toast.error("Failed to load templates");
    else setTemplates(data ?? []);
    setLoading(false);
  }

  useEffect(() => {
    loadTemplates();
  }, [user?.id]); // eslint-disable-line

  // ─── Panel helpers ──────────────────────────────────────────────────────────

  function openEdit(tpl: Template) {
    setEditing({
      id: tpl.id,
      display_name: tpl.display_name,
      header_media_url: tpl.header_media_url ?? "",
      template_body: tpl.template_body ?? "",
      name: tpl.name,
      language_code: tpl.language_code,
      placeholders: tpl.placeholders ?? [],
    });
    setPanelOpen(true);
  }

  function closePanel() {
    setPanelOpen(false);
    setEditing(null);
  }

  // ─── Image upload ───────────────────────────────────────────────────────────

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (!editing || !user) return;
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const path = `${user.id}/${Date.now()}_${file.name}`;
    const { error: uploadError } = await supabase.storage
      .from("template-media")
      .upload(path, file, { upsert: true, contentType: file.type });
    if (uploadError) {
      toast.error("Upload error: " + uploadError.message);
      setUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from("template-media").getPublicUrl(path);
    setEditing(prev => prev ? { ...prev, header_media_url: urlData.publicUrl } : prev);
    setUploading(false);
    toast.success("Image uploaded");
  }

  // ─── Save ───────────────────────────────────────────────────────────────────

  async function handleSave() {
    if (!editing) return;
    if (!editing.display_name.trim()) {
      toast.error("Display name is required");
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("clinic_templates")
      .update({
        display_name: editing.display_name.trim(),
        header_media_url: editing.header_media_url.trim(),
      })
      .eq("id", editing.id);
    if (error) {
      toast.error("Error: " + error.message);
    } else {
      toast.success("Template updated");
      closePanel();
      loadTemplates();
    }
    setSaving(false);
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">

      {/* Header */}
      {!embedded && (
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">WhatsApp Templates</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Templates available for sending messages to your patients
          </p>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : templates.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <p className="text-base font-medium">No templates yet</p>
          <p className="text-sm mt-1">Contact your administrator to set up templates.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-4 py-3 font-medium text-slate-600">Display name</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Message body</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Image</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {templates.map((tpl, i) => (
                <tr
                  key={tpl.id}
                  className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${i === templates.length - 1 ? "border-b-0" : ""}`}
                >
                  <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">
                    {tpl.display_name}
                  </td>
                  <td className="px-4 py-3 max-w-sm">
                    <p className="text-xs text-slate-500 font-mono whitespace-pre-wrap line-clamp-3" title={tpl.template_body}>
                      {tpl.template_body || "—"}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    {tpl.header_media_url ? (
                      <img
                        src={tpl.header_media_url}
                        alt="header"
                        className="w-10 h-10 object-cover rounded-lg border border-slate-200"
                      />
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end">
                      <button
                        onClick={() => openEdit(tpl)}
                        className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                        title="Edit display name or image"
                      >
                        <Pencil size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit drawer */}
      {panelOpen && editing && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/30 backdrop-blur-sm" onClick={closePanel} />
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl flex flex-col">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-base font-semibold text-slate-800">Edit template</h2>
              <button
                onClick={closePanel}
                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Form */}
            <div className="flex-1 px-6 py-5 space-y-5">

              {/* Display name — editable */}
              <Field label="Display name" required hint="The label shown in this backoffice when selecting a template">
                <input
                  type="text"
                  value={editing.display_name}
                  onChange={e => setEditing(prev => prev ? { ...prev, display_name: e.target.value } : prev)}
                  placeholder="e.g. Appointment reminder"
                  className={inputClass}
                />
              </Field>

              {/* Header image — editable */}
              <Field label="Header image" hint="Image displayed at the top of the WhatsApp message">
                <div className="space-y-2">
                  {editing.header_media_url && (
                    <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <img
                        src={editing.header_media_url}
                        alt="preview"
                        className="w-16 h-16 object-cover rounded-lg border border-slate-200 shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500 break-all leading-relaxed">{editing.header_media_url}</p>
                        <button
                          onClick={() => setEditing(prev => prev ? { ...prev, header_media_url: "" } : prev)}
                          className="mt-1.5 text-xs text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 hover:border-slate-400 hover:bg-slate-50 text-slate-600 text-sm rounded-lg transition-colors w-full justify-center"
                  >
                    <ImagePlus size={16} />
                    {uploading ? "Uploading…" : editing.header_media_url ? "Change image" : "Upload image"}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                  <input
                    type="text"
                    value={editing.header_media_url}
                    onChange={e => setEditing(prev => prev ? { ...prev, header_media_url: e.target.value } : prev)}
                    placeholder="or paste a public URL directly"
                    className={`${inputClass} text-xs`}
                  />
                </div>
              </Field>

              {/* Divider */}
              <div className="border-t border-slate-100 pt-4">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-4">Template info (read-only)</p>
                <div className="space-y-3">
                  <ReadOnlyField label="Meta template name" value={editing.name} mono />
                  <ReadOnlyField label="Language" value={editing.language_code || "—"} />
                  {editing.placeholders.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-1.5">Variables</p>
                      <div className="flex flex-wrap gap-1.5">
                        {editing.placeholders.map(p => (
                          <span key={p} className="bg-slate-100 text-slate-600 text-xs px-2.5 py-1 rounded-full font-mono">
                            {`{{${p}}}`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {editing.template_body && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-1.5">Message body</p>
                      <pre className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-200 whitespace-pre-wrap font-mono leading-relaxed">
                        {editing.template_body}
                      </pre>
                    </div>
                  )}
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={closePanel}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── UI helpers ───────────────────────────────────────────────────────────────

const inputClass =
  "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent transition";

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {hint && <p className="text-xs text-slate-400 leading-snug">{hint}</p>}
      {children}
    </div>
  );
}

function ReadOnlyField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
      <p className={`text-sm text-slate-700 ${mono ? "font-mono" : ""}`}>{value}</p>
    </div>
  );
}
